import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Space, Input, Card, Tag, Row, Col, Switch, Spin, Badge, Tabs, Segmented, App } from 'antd';
import { PlusOutlined, ImportOutlined, SearchOutlined, GlobalOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AssetCategory, AssetProvider, AssetListItem, CategoryConfig } from '@mygate/common';
import { assetManagementService } from '@/services/asset-management';
import { TradevestImportModal } from '@/components/TradevestImportModal';
import { TradevestCatalogBrowser } from '@/components/TradevestCatalogBrowser';

const { Text } = Typography;

const PROVIDER_LABELS: Record<AssetProvider, { label: string; color: string }> = {
  [AssetProvider.TANGANY]: { label: 'Tangany', color: '#1890ff' },
  [AssetProvider.TRADEVEST]: { label: 'Tradevest', color: '#52c41a' },
  [AssetProvider.SELF_CUSTODY]: { label: 'Self-Custody', color: '#faad14' },
};

const CATEGORY_COLORS: Record<AssetCategory, string> = {
  [AssetCategory.CRYPTO]: 'blue',
  [AssetCategory.ETF]: 'green',
  [AssetCategory.COMMODITY]: 'gold',
  [AssetCategory.RWA]: 'purple',
};

export function AssetManagementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const [view, setView] = useState<'internal' | 'tradevest'>('internal');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [importModalVisible, setImportModalVisible] = useState(false);

  const { data: assetsResponse, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets', activeCategory, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit: 200 };
      if (activeCategory !== 'all') params.category = activeCategory;
      if (search) params.search = search;
      const response = await assetManagementService.listAssets(params);
      return response.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: categoriesResponse } = useQuery({
    queryKey: ['asset-categories'],
    queryFn: async () => {
      const response = await assetManagementService.listCategories();
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const assets = assetsResponse?.assets ?? [];
  const categories = (categoriesResponse ?? []) as CategoryConfig[];

  /**
   * Switching a category off does more than hide a tab: its assets leave the app catalog entirely,
   * and a client that classifies holdings against that catalog drops the rows too. Worth a sentence
   * before someone flips it on a category people already hold.
   */
  const applyCategoryToggle = async (category: AssetCategory, enabled: boolean) => {
    try {
      await assetManagementService.updateCategory(category, { isEnabled: enabled });
      queryClient.invalidateQueries({ queryKey: ['asset-categories'] });
      message.success(`${category} category ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      message.error('Failed to update category');
    }
  };

  const handleCategoryToggle = async (category: AssetCategory, enabled: boolean) => {
    if (enabled) {
      await applyCategoryToggle(category, true);
      return;
    }

    // Only countable when the grid is showing everything; a filtered view would understate it.
    const showingEverything = activeCategory === 'all' && search === '';
    const activeCount = showingEverything
      ? assets.filter(a => a.category === category && a.isActive).length
      : undefined;

    modal.confirm({
      title: `Disable the ${category} category?`,
      content: (
        <Space direction="vertical" size={4}>
          <Text>
            {activeCount === undefined
              ? 'Every active asset in this category disappears from the app catalog.'
              : `${activeCount} active asset(s) in this category disappear from the app catalog.`}
          </Text>
          <Text type="secondary">
            Holdings are matched against that catalog, so a user who owns one stops seeing it in their portfolio until
            the category is switched back on.
          </Text>
        </Space>
      ),
      okText: 'Disable',
      okButtonProps: { danger: true },
      onOk: () => applyCategoryToggle(category, false),
    });
  };

  const handleImportComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

  const renderProviderDots = (asset: AssetListItem) => {
    const allProviders = Object.values(AssetProvider);
    return (
      <Space wrap size={4}>
        {allProviders.map(provider => {
          const link = asset.providers?.find(p => p.providerId === provider);
          const config = PROVIDER_LABELS[provider];
          return (
            <Tag
              key={provider}
              style={{
                fontSize: 10,
                padding: '0 4px',
                lineHeight: '18px',
                opacity: link ? 1 : 0.3,
              }}
              color={link?.isEnabled ? config.color : 'default'}
            >
              {config.label}
            </Tag>
          );
        })}
      </Space>
    );
  };

  const renderAssetCard = (asset: AssetListItem) => (
    <Col xs={24} md={12} xl={8} xxl={6} key={asset._id}>
      <Card
        hoverable
        role="link"
        tabIndex={0}
        aria-label={`${asset.symbol} – ${asset.name} öffnen`}
        onKeyDown={event => {
          if (event.key === 'Enter') navigate(`/assets/${asset._id}`);
        }}
        onClick={() => navigate(`/assets/${asset._id}`)}
        style={{ height: '100%' }}
        styles={{ body: { padding: 16 } }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space wrap align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space wrap>
              {asset.logoUrl ? (
                <img
                  src={asset.logoUrl}
                  alt={asset.symbol}
                  style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }}
                  onError={e => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 14,
                    color: '#999',
                  }}
                >
                  {asset.symbol.slice(0, 2)}
                </div>
              )}
              <div>
                <Text strong style={{ fontSize: 16 }}>
                  {asset.symbol}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                  {asset.name}
                </Text>
              </div>
            </Space>
            <Badge status={asset.isActive ? 'success' : 'default'} />
          </Space>

          <Space size={4} wrap>
            <Tag color={CATEGORY_COLORS[asset.category]} style={{ margin: 0 }}>
              {asset.category}
            </Tag>
          </Space>

          {renderProviderDots(asset)}
        </Space>
      </Card>
    </Col>
  );

  const tabItems = [
    { key: 'all', label: `All (${assetsResponse?.total ?? 0})` },
    ...Object.values(AssetCategory).map(cat => ({
      key: cat,
      label: cat.charAt(0) + cat.slice(1).toLowerCase(),
    })),
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Assets und externe Kataloge verwalten.</Typography.Text>
        {view === 'internal' && (
          <Space wrap>
            <Button icon={<GlobalOutlined />} onClick={() => navigate('/issuers')}>
              Issuers
            </Button>
            <Button icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}>
              Import from Tradevest
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/assets/new')}>
              Add Asset
            </Button>
          </Space>
        )}
      </div>

      <div style={{ maxWidth: 1400 }}>
        <Segmented
          style={{ marginBottom: 16 }}
          value={view}
          onChange={v => setView(v as 'internal' | 'tradevest')}
          options={[
            { label: 'Internal Catalog', value: 'internal' },
            { label: 'Tradevest Catalog (live)', value: 'tradevest' },
          ]}
        />
        {view === 'tradevest' ? (
          <TradevestCatalogBrowser />
        ) : (
          <>
            {/* Category toggles */}
            {categories.length > 0 && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap size="large">
                  <Text strong>Categories:</Text>
                  {categories.map(cat => (
                    <Space wrap key={cat.category} size={8}>
                      <Switch
                        size="small"
                        checked={cat.isEnabled}
                        onChange={checked => handleCategoryToggle(cat.category, checked)}
                      />
                      <Tag color={CATEGORY_COLORS[cat.category]}>{cat.displayName || cat.category}</Tag>
                    </Space>
                  ))}
                </Space>
              </Card>
            )}

            {/* Search + Tabs */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Input
                  placeholder="Search by symbol or name..."
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  allowClear
                  style={{ maxWidth: 400 }}
                />
                <Tabs
                  activeKey={activeCategory}
                  onChange={setActiveCategory}
                  items={tabItems}
                  size="small"
                  style={{ marginBottom: 0 }}
                />
              </Space>
            </Card>

            {/* Asset grid */}
            {assetsLoading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
              </div>
            ) : assets.length === 0 ? (
              <Card style={{ textAlign: 'center', padding: 48 }}>
                <Text type="secondary">No assets found. Create one or import from Tradevest.</Text>
              </Card>
            ) : (
              <Row gutter={[16, 16]}>{assets.map(renderAssetCard)}</Row>
            )}
          </>
        )}
      </div>

      <TradevestImportModal
        visible={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImportComplete={handleImportComplete}
      />
    </>
  );
}
